import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, RotateCcw, Eye, Save, X } from "lucide-react";
import { toast } from "sonner";
import { useIdentidade, aplicarCores, urlArquivo, CORES_PADRAO } from "@/lib/identidade";
import logoDark from "@/assets/logo-dark-t.png";
import logoLight from "@/assets/logo-light.png";
import logoHoriz from "@/assets/logo-horizontal.png";

const SLOTS = [
  ["imagem_entrada", "Imagem de entrada e painel", "Logo oficial com “Gerenciamento de Contratos” (fundo escuro). As margens externas vazias são removidas automaticamente ao salvar.", logoDark, "bg-[var(--fundo-marca,#000617)]"],
  ["icone", "Ícone do aplicativo", "Quadrado; usado em avisos, favicon, manifesto e ícone do app instalado", logoLight, "bg-white"],
  ["logo_contratos", "Logo TRT dos contratos (PDF)", "Horizontal, preparada para fundo branco", logoHoriz, "bg-white"],
];
const PLANOS = { basico: "Básico", profissional: "Profissional", empresarial: "Master" };

function SlotImagem({ tipo, titulo, desc, padrao, bg, atual, nova, onChange }) {
  const ref = useRef(null);
  const src = nova === "" ? padrao : nova || atual || padrao;
  const pick = (f) => {
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("Imagem acima de 3 MB"); return; }
    const r = new FileReader(); r.onload = () => onChange(r.result); r.readAsDataURL(f);
  };
  return (
    <div className="trt-card p-4 space-y-2" data-testid={`ident-slot-${tipo}`}>
      <div className="font-semibold text-sm">{titulo}</div>
      <p className="text-xs text-slate-500">{desc}</p>
      <div className={`${bg} rounded-xl border h-28 flex items-center justify-center p-2`}><img src={src} alt={titulo} className="max-h-full max-w-full object-contain" data-testid={`ident-preview-${tipo}`} /></div>
      <div className="flex flex-wrap gap-2 items-center">
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid={`ident-file-${tipo}`} />
        <Button size="sm" variant="outline" onClick={() => ref.current?.click()} className="gap-1" data-testid={`ident-alterar-${tipo}`}><Upload className="w-3.5 h-3.5" /> Alterar imagem</Button>
        {(nova || (atual && nova !== "")) && <Button size="sm" variant="ghost" onClick={() => onChange("")} className="gap-1 text-red-600" data-testid={`ident-remover-${tipo}`}><X className="w-3.5 h-3.5" /> Usar padrão</Button>}
        {nova && <Badge className="bg-amber-100 text-amber-800 border-0">não salvo</Badge>}
        {!nova && atual && <Badge className="bg-emerald-100 text-emerald-700 border-0">personalizada</Badge>}
      </div>
    </div>
  );
}

export function IdentidadeVisual() {
  const ident = useIdentidade();
  const [data, setData] = useState(null);
  const [novas, setNovas] = useState({});
  const [cores, setCores] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previa, setPrevia] = useState(false);
  const load = () => api.get("/identidade/admin").then((r) => { setData(r.data); setCores(r.data.cores); });
  useEffect(() => { load(); }, []);
  useEffect(() => () => aplicarCores(ident.cores), []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data || !cores) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin gold-text" /></div>;

  const sujo = Object.keys(novas).length > 0 || JSON.stringify(cores) !== JSON.stringify(data.cores);
  const visualizar = () => { aplicarCores(cores); setPrevia(true); toast.info("Prévia aplicada nesta tela. Nada foi salvo ainda."); };
  const cancelarPrevia = () => { aplicarCores(data.cores); setCores(data.cores); setNovas({}); setPrevia(false); };
  const salvar = async () => {
    setSaving(true);
    try {
      await api.put("/identidade", { cores, ...novas });
      await ident.recarregar(); await load(); setNovas({}); setPrevia(false);
      toast.success("Identidade visual salva e aplicada ao aplicativo e aos novos PDFs.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };
  const restaurar = async () => {
    if (!window.confirm("Restaurar o padrão TRT (imagens oficiais e cores azul-marinho/azul)? As personalizações atuais serão removidas.")) return;
    setSaving(true);
    try { await api.post("/identidade/restaurar"); await ident.recarregar(); await load(); setNovas({}); setPrevia(false); toast.success("Padrão TRT restaurado."); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao restaurar"); }
    finally { setSaving(false); }
  };
  const setLogoPropria = async (emp, v) => {
    const valor = v === "padrao" ? null : v === "sim";
    try {
      await api.put(`/empresas/${emp.id}/logo-propria`, { valor });
      setData((d) => ({ ...d, empresas: d.empresas.map((e) => (e.id === emp.id ? { ...e, logo_propria_excecao: valor } : e)) }));
      toast.success(`${emp.nome}: logo própria nos contratos ${valor === null ? "conforme o plano" : valor ? "liberada" : "bloqueada"}.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const atual = (t) => (data.imagens?.[t] ? urlArquivo(t, data.versao) : null);

  return (
    <div className="space-y-5" data-testid="identidade-visual">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h2 className="text-lg font-extrabold">Identidade visual</h2>
          <p className="text-slate-500 text-sm">Imagens e cores da plataforma TRT GESTÃO. Vale para a entrada, o painel, os avisos e o rodapé/cabeçalho dos novos PDFs. Documentos já emitidos não mudam.</p>
          <p className="text-xs text-slate-500 mt-1" data-testid="ident-aviso-icone">Ícone: ao salvar, o favicon, o manifesto e os tamanhos 180/192/512 passam a usar a nova imagem (com versão para evitar cache). O ícone de um app <b>já instalado</b> depende do navegador/sistema: o Chrome/Android atualiza em até alguns dias ao abrir o app; no iPhone é preciso remover e adicionar o atalho novamente.</p>
          {data.atualizado_em && <p className="text-xs text-slate-400 mt-1" data-testid="ident-atualizado">Última alteração: {new Date(data.atualizado_em).toLocaleString("pt-BR")} por {data.atualizado_por}</p>}</div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={visualizar} disabled={!sujo && previa} className="gap-1" data-testid="ident-visualizar"><Eye className="w-4 h-4" /> Visualizar</Button>
          {(previa || sujo) && <Button variant="ghost" onClick={cancelarPrevia} data-testid="ident-cancelar">Descartar</Button>}
          <Button onClick={salvar} disabled={saving || !sujo} className="bg-onyx text-white gap-1" data-testid="ident-salvar">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvar</Button>
          <Button variant="outline" onClick={restaurar} disabled={saving || !data.personalizado} className="gap-1" data-testid="ident-restaurar"><RotateCcw className="w-4 h-4" /> Restaurar padrão</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        {SLOTS.map(([t, titulo, desc, padrao, bg]) => <SlotImagem key={t} tipo={t} titulo={titulo} desc={desc} padrao={padrao} bg={bg} atual={atual(t)} nova={novas[t]} onChange={(v) => setNovas((p) => ({ ...p, [t]: v }))} />)}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="trt-card p-4 space-y-3" data-testid="ident-cores">
          <div className="font-semibold text-sm">Cores do sistema</div>
          {[["primaria", "Cor principal (fundos, menu, botões escuros)"], ["acento", "Cor de destaque (links, ícones, botão Entrar)"]].map(([k, l]) => (
            <div key={k} className="flex items-center gap-3">
              <input type="color" value={cores[k]} onChange={(e) => setCores((c) => ({ ...c, [k]: e.target.value }))} className="h-10 w-12 rounded border cursor-pointer" data-testid={`ident-cor-${k}`} />
              <div className="flex-1 min-w-0"><Label className="text-xs">{l}</Label>
                <Input value={cores[k]} onChange={(e) => setCores((c) => ({ ...c, [k]: e.target.value }))} className="mt-1 h-9 mono" data-testid={`ident-cor-hex-${k}`} /></div>
            </div>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setCores(CORES_PADRAO)} data-testid="ident-cores-padrao">Cores padrão TRT</Button>
        </div>
        <div className="trt-card p-4 space-y-2" data-testid="ident-previa">
          <div className="font-semibold text-sm">Pré-visualização</div>
          <div className="rounded-xl p-4 flex flex-col items-center gap-2" style={{ background: cores.primaria }}>
            <img src={novas.imagem_entrada === "" ? logoDark : novas.imagem_entrada || atual("imagem_entrada") || logoDark} alt="Entrada" className="h-16 object-contain" />
            <div className="text-white font-extrabold text-sm">TRT GESTÃO</div>
            <div className="h-7 w-32 rounded-md text-white text-xs flex items-center justify-center" style={{ background: cores.acento }}>Entrar</div>
          </div>
          <div className="rounded-xl border bg-white p-3 text-[10px] text-slate-500 space-y-1">
            <img src={novas.logo_contratos === "" ? logoHoriz : novas.logo_contratos || atual("logo_contratos") || logoHoriz} alt="Contrato" className="h-8 object-contain mx-auto" />
            <div className="text-center font-bold text-slate-800">CONTRATO</div>
            <div className="h-1.5 bg-slate-100 rounded" /><div className="h-1.5 bg-slate-100 rounded w-4/5" />
            <div className="flex items-center gap-1 pt-1 border-t"><img src={novas.logo_contratos === "" ? logoHoriz : novas.logo_contratos || atual("logo_contratos") || logoHoriz} alt="" className="h-3" /> Gerado por TRT Gerenciamento de Contratos</div>
          </div>
        </div>
      </div>

      <div className="trt-card overflow-hidden" data-testid="ident-empresas">
        <div className="p-4 border-b"><div className="font-semibold text-sm">Logo própria nos contratos, por empresa</div>
          <p className="text-xs text-slate-500">Padrão: Básico usa a logo TRT; Profissional e Master usam a logo da empresa quando cadastrada. A exceção aqui não altera plano nem benefícios. Em todos os planos o rodapé TRT é mantido.</p></div>
        <div className="divide-y">
          {data.empresas.map((e) => {
            const v = e.logo_propria_excecao === null || e.logo_propria_excecao === undefined ? "padrao" : e.logo_propria_excecao ? "sim" : "nao";
            const efetivo = v === "padrao" ? e.padrao_plano : v === "sim";
            return (
              <div key={e.id} className="p-3 flex flex-wrap items-center gap-2 sm:gap-3" data-testid={`ident-emp-${e.slug}`}>
                <div className="flex-1 min-w-[10rem]"><div className="font-medium text-sm truncate">{e.nome}</div>
                  <div className="text-xs text-slate-500">{PLANOS[e.plano_id] || e.plano_id || "—"} · {e.tem_logo ? "logo cadastrada" : "sem logo cadastrada"} · efetivo: <b>{efetivo ? "logo da empresa" : "logo TRT"}</b></div></div>
                <Select value={v} onValueChange={(x) => setLogoPropria(e, x)}>
                  <SelectTrigger className="w-full sm:w-52 h-9" data-testid={`ident-emp-select-${e.slug}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="padrao">Padrão do plano</SelectItem><SelectItem value="sim">Permitir logo própria</SelectItem><SelectItem value="nao">Bloquear (usar logo TRT)</SelectItem></SelectContent>
                </Select>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
